import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CreateAndEditProjectModal from '@features/project-management-tools/components/CreateAndEditProjectModal';
import MyProjectKanbanBoard from '@features/project-management-tools/components/MyProjectKanbanBoard';
import MyProjectTopbar from '@features/project-management-tools/components/MyProjectTopbar';
import {
	useDeleteProjectMutation,
	useGetAllProjectsMutation,
	useGetMyProjectFilterOptionsMutation
} from '@features/project-management-tools/api/projectManagementApi';
import {
	setCompletedProjects,
	setInProgressProjects,
	setIsShowCreateProjectModal,
	setIsShowDeleteProjectModal,
	setNotStartedProjects,
	setSelectedProjectIdToDelete,
	setSelectedProjectToEdit
} from '@features/project-management-tools/redux/myProjectSlice';
import { createFilterRequestBody } from '@features/project-management-tools/utils';

import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
import { toastError, toastSuccess } from '@/components/Toast';
import { useMyProject } from '@/store/reducers';
import { isPageReachBottom } from '@/utils/Util';
import FilterComponent from '@/features/filter/v2/FilterComponent';
import { allFilterPages, myProjectsFilterFields } from '@/features/filter/static/filterUtil';
import { Types } from '@/store/types';

const initialStateForProjects = {
	notStartedProjects: { data: [], totalElements: 0, currentPage: 0 },
	inProgressProjects: { data: [], totalElements: 0, currentPage: 0 },
	completedProjects: { data: [], totalElements: 0, currentPage: 0 },
	selectedProjectToEdit: null
};

const MyProjectPage = () => {
	const [page, setPage] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const isLoadingRef = useRef(false);
	const dispatch = useDispatch();
	const [isShowFilter, setIsShowFilter] = useState(false);
	const [search, setSearch] = useState('');
	const filterfields = useSelector((store) => store.filterFieldStore);

	const [getAllProjects, { data: allProjectsList }] = useGetAllProjectsMutation({ page, limit: 20 });

	const [getMyProjectFilterOptions] = useGetMyProjectFilterOptionsMutation();

	const {
		isShowCreateProjectModal,
		isEditProject,
		isShowDeleteProjectModal,
		selectedProjectIdToDelete,
		selectedProjectToEdit
	} = useMyProject();

	const [deleteProject] = useDeleteProjectMutation();

	const projectContainerRef = useRef(null);
	const pendingProjectsRef = useRef(initialStateForProjects.notStartedProjects);
	const inProgressProjectsRef = useRef(initialStateForProjects.inProgressProjects);
	const completedProjectsRef = useRef(initialStateForProjects.completedProjects);

	// reset all project states and refs
	const resetProjectsStateAndRefs = () => {
		setPage(0);
		pendingProjectsRef.current = { ...initialStateForProjects.notStartedProjects };
		inProgressProjectsRef.current = { ...initialStateForProjects.inProgressProjects };
		completedProjectsRef.current = { ...initialStateForProjects.completedProjects };
		dispatch(setNotStartedProjects({ ...initialStateForProjects.notStartedProjects }));
		dispatch(setInProgressProjects({ ...initialStateForProjects.inProgressProjects }));
		dispatch(setCompletedProjects({ ...initialStateForProjects.completedProjects }));
		isLoadingRef.current = false;
	};

	useEffect(() => {
		isLoadingRef.current = isLoading;
	}, [isLoading]);

	useEffect(() => {
		const requestBody = createFilterRequestBody(filterfields);

		getAllProjects({
			body: { statusList: ['PENDING', 'RUNNING', 'COMPLETED'], search, ...requestBody },
			params: { page, size: 20 }
		})
			.unwrap()
			.then((response) => {
				const pending = response?.PENDING;
				const running = response?.RUNNING;
				const completed = response?.COMPLETED;

				updateProjectsRef(pending, pendingProjectsRef);
				updateProjectsRef(running, inProgressProjectsRef);
				updateProjectsRef(completed, completedProjectsRef);
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong!');
			});
	}, [page, search, filterfields]);

	// Reset the state when the component unmounts
	useEffect(() => {
		return () => {
			resetProjectsStateAndRefs();
		};
	}, []);

	const updateProjectsRef = (data, currentRef) => {
		if (currentRef?.totalPages === page) return;

		if (page > 0) {
			currentRef.current = {
				...data,
				data: [...currentRef.current.data, ...data.data]
			};
		} else {
			currentRef.current = data;
		}
	};

	const handleConfirmDeleteProject = () => {
		deleteProject(selectedProjectIdToDelete)
			.unwrap()
			.then(() => {
				setPage(0);
				getAllProjects({
					body: {
						statusList: ['PENDING', 'RUNNING', 'COMPLETED']
					},
					params: { page: 0, size: 20 }
				});
				toastSuccess('Project deleted successfully');
				dispatch(setIsShowDeleteProjectModal(false));
				dispatch(setSelectedProjectIdToDelete(null));
			})
			.catch((error) => {
				toastError(error);
			});
	};

	const handleScroll = () => {
		if (isPageReachBottom() && !isLoadingRef.current) {
			const notStartedHasMore =
				pendingProjectsRef.current?.totalElements > pendingProjectsRef.current?.data?.length &&
				page < pendingProjectsRef.current?.totalPages - 1;

			const inProgressHasMore =
				inProgressProjectsRef.current?.totalElements > inProgressProjectsRef.current?.data?.length &&
				page < inProgressProjectsRef.current?.totalPages - 1;

			const completedHasMore =
				completedProjectsRef.current?.totalElements > completedProjectsRef.current?.data?.length &&
				page < completedProjectsRef.current?.totalPages - 1;

			if (notStartedHasMore || inProgressHasMore || completedHasMore) {
				setIsLoading(true);
				isLoadingRef.current = true;
				setPage((prevPage) => prevPage + 1);
			}
		}
	};

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	useEffect(() => {
		if (isShowFilter) {
			const requestBody = createFilterRequestBody(filterfields);
			if (Object.keys(requestBody)?.length > 0) {
				resetProjectsStateAndRefs();
			}
			getMyProjectFilterOptions({ body: requestBody })
				.unwrap()
				.then((response) => {
					dispatch({
						type: Types.STORE_DEPENDENT_FILTER_LIST,
						payload: { responseData: response, type: 'MY_PROJECTS' }
					});
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong!');
				});
		}
	}, [isShowFilter, filterfields]);

	const handleFilterToggle = () => {
		setIsShowFilter((prev) => !prev);
	};

	return (
		<>
			<MyProjectTopbar
				projectCount={
					(allProjectsList?.PENDING?.totalElements || 0) +
					(allProjectsList?.RUNNING?.totalElements || 0) +
					(allProjectsList?.COMPLETED?.totalElements || 0)
				}
				onOpenCreateProjectModal={() => dispatch(setIsShowCreateProjectModal(true))}
				onFilterToggle={handleFilterToggle}
				onSearch={setSearch}
			/>
			<section
				className="filter-data-container filter-v3-data-container bg-white task-content"
				ref={projectContainerRef}
			>
				<FilterComponent
					filterPageName={allFilterPages.MY_PROJECTS}
					fields={myProjectsFilterFields}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>
				<div className="data-table">
					<div className="task-list-container">
						<div className="task-all-row d-grid gap-5 grid-columns-4">
							<MyProjectKanbanBoard />
						</div>
					</div>
					{isShowCreateProjectModal && (
						<CreateAndEditProjectModal
							onClose={() => {
								dispatch(setSelectedProjectToEdit(null));
								dispatch(setIsShowCreateProjectModal(false));
							}}
							isEdit={isEditProject}
							selectedProjectInfo={selectedProjectToEdit}
							onUpdateProjectSuccess={() => {
								setPage(0);
								getAllProjects({
									body: {
										statusList: ['PENDING', 'RUNNING', 'COMPLETED']
									},
									params: { page: 0, size: 20 }
								});
							}}
						/>
					)}
					{isShowDeleteProjectModal && (
						<DeleteConfirmationModal
							onClose={() => dispatch(setIsShowDeleteProjectModal(false))}
							onConfirm={handleConfirmDeleteProject}
							title="Remove Project"
							description="Are you sure about removing this project?"
						/>
					)}
				</div>
			</section>
		</>
	);
};

export default MyProjectPage;
